import { ref, h, render } from 'vue'
import UiModalConfirm from '@/components/ui/ModalConfirm.vue'
import { useNuxtApp } from '#app'

/**
 * Use this composable in any component or composable:
 * `const { confirm } = useUiConfirm()`
 * await confirm('Delete diary?')
 */
export function useUiModalConfirm() {
  // Reusable root element for teleport targets
  const root = ref<HTMLElement | null>(null)

  const cleanup = () => {
    if (root.value && root.value.parentNode) {
      root.value.parentNode.removeChild(root.value)
      root.value = null
    }
  }

  /**
   * Shows confirm dialog and resolves promise
   * @param message string – text inside dialog
   * @param okText  string – label for OK button
   * @param cancelText string – label for Cancel button
   */
  async function confirm(
    message: string,
    okText = 'OK',
    cancelText = 'Cancel'
  ): Promise<boolean> {
    // Lazily create root node once
    if (!root.value) {
      root.value = document.createElement('div')
      document.body.appendChild(root.value)
    }

    return new Promise<boolean>((resolve) => {
      // Get Nuxt application instance to provide proper app context
      const nuxtApp = useNuxtApp()

      // Render modal into root element
      const vnode = h(UiModalConfirm, {
        modelValue: true,
        message,
        okText,
        cancelText,
        // When user clicks either button, resolve and unmount
        onClose(result: boolean) {
          resolve(result)
          render(null, root.value!) // Unmount component
        },
      })

      // Attach Nuxt app context so that useNuxtApp() works inside the modal (fixes mobile / ClientOnly errors)
      vnode.appContext = nuxtApp.vueApp._context

      render(vnode, root.value)
    })
  }

  return { confirm, cleanup }
}